import { memo } from "preact/compat";
import { useTranslation } from "react-i18next";
import { Typography } from "../../../shared/ui";
import { SendAttempt } from "../model/types";
import css from "./ChatAttemptItem.module.scss";

/**
 * Строка неподтверждённой отправки. Живёт отдельным лейном под списком:
 * ACK не является записью чата и в projection не вставляется.
 */

interface ChatAttemptItemProps {
  attempt: SendAttempt;
  onRetry: (clientMessageId: string) => void;
}

/** Безопасные ключи. Raw `error_message` с бэкенда в UI не попадает. */
const ERROR_KEYS: Record<string, string> = {
  EMPTY_MESSAGE: "chat.error_empty_message",
  CONTROL_CHAR: "chat.error_control_char",
  MESSAGE_TOO_LONG: "chat.error_message_too_long",
  MESSAGE_TOO_LARGE: "chat.error_message_too_long",
  RATE_LIMIT: "chat.error_rate_limit",
  CHAT_DISABLED: "chat.error_chat_disabled",
};

function statusKey(attempt: SendAttempt): string {
  switch (attempt.state) {
    case "pending":
      return "chat.status_sending";
    case "acknowledged":
      return "chat.status_sent";
    case "rejected":
      return ERROR_KEYS[attempt.errorCode ?? ""] ?? "chat.status_failed";
    case "durability-unknown":
      return "chat.status_unconfirmed";
  }
}

function ChatAttemptItemComponent({ attempt, onRetry }: ChatAttemptItemProps) {
  const { t } = useTranslation();

  const canRetry =
    attempt.state === "rejected" || attempt.state === "durability-unknown";

  return (
    <div className={`${css.row} ${css[attempt.state]}`}>
      <div className={css.bubble}>
        <Typography variant="caption2" color="white" className={css.text}>
          {attempt.text}
        </Typography>

        <div className={css.footer}>
          <Typography variant="caption3" color={canRetry ? "orange" : "white"}>
            {t(statusKey(attempt))}
          </Typography>

          {canRetry && (
            <button
              type="button"
              className={css.action}
              onClick={() => onRetry(attempt.clientMessageId)}
            >
              <Typography variant="caption3" color="orange">
                {t("chat.retry")}
              </Typography>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export const ChatAttemptItem = memo(ChatAttemptItemComponent);
